import React, { useState } from 'react';
import { useDeals } from '../context/DealsContext';
import { useQuota } from '../context/QuotaContext';
import FuelGauge from './FuelGauge';
import { FiCalendar } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const GoalTracker = () => {
  const { deals } = useDeals();
  const { getAdjustedQuota } = useQuota();
  const [periodView, setPeriodView] = useState('monthly');

  // Get the current date
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const currentQuarter = Math.floor(currentMonth / 3);

  // Filter deals by closed won status and time period
  const closedWonDeals = deals.filter(deal => deal.stage === 'Closed Won');

  // Filter deals by selected period
  const getDealsInPeriod = (period) => {
    return closedWonDeals.filter(deal => {
      const dealDate = new Date(deal.lastActivity);
      const dealYear = dealDate.getFullYear();
      const dealMonth = dealDate.getMonth();
      const dealQuarter = Math.floor(dealMonth / 3);

      switch (period) {
        case 'monthly':
          return dealYear === currentYear && dealMonth === currentMonth;
        case 'quarterly':
          return dealYear === currentYear && dealQuarter === currentQuarter;
        case 'yearly':
          return dealYear === currentYear;
        default:
          return true;
      }
    });
  };

  // Calculate the total value of closed won deals for the selected period
  const getClosedWonValue = (period) => {
    const filteredDeals = getDealsInPeriod(period);
    return filteredDeals.reduce((sum, deal) => sum + deal.value, 0);
  };

  // Get period-specific data
  const periodData = {
    monthly: {
      closedValue: getClosedWonValue('monthly'),
      quota: getAdjustedQuota('monthly'),
      label: 'Monthly',
      subtitle: new Date().toLocaleString('default', { month: 'long', year: 'numeric' })
    },
    quarterly: {
      closedValue: getClosedWonValue('quarterly'),
      quota: getAdjustedQuota('quarterly'),
      label: 'Quarterly',
      subtitle: `Q${currentQuarter + 1} ${currentYear}`
    },
    yearly: {
      closedValue: getClosedWonValue('yearly'),
      quota: getAdjustedQuota('yearly'),
      label: 'Yearly',
      subtitle: `${currentYear}`
    }
  };

  // Calculate percentage of quota achieved
  const currentData = periodData[periodView];
  const percentage = currentData.quota > 0 ? (currentData.closedValue / currentData.quota) * 100 : 0;

  // Format currency
  const formatCurrency = (value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toLocaleString()}`;
  };

  // Get status classes based on percentage
  const getStatusClasses = () => {
    if (percentage >= 75)
      return {
        text: "text-green-700 dark:text-green-500",
        bg: "bg-green-50 dark:bg-green-900/30",
        border: "border-green-200 dark:border-green-800/50"
      };
    if (percentage >= 50)
      return {
        text: "text-amber-700 dark:text-amber-500",
        bg: "bg-amber-50 dark:bg-amber-900/30",
        border: "border-amber-200 dark:border-amber-800/50"
      };
    return {
      text: "text-red-700 dark:text-red-500",
      bg: "bg-red-50 dark:bg-red-900/30",
      border: "border-red-200 dark:border-red-800/50"
    };
  };

  const statusClasses = getStatusClasses();

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Closed Deals to Goal Ratio
        </h3>
        <div className="flex items-center space-x-2">
          <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg overflow-hidden flex">
            {['monthly', 'quarterly', 'yearly'].map(period => (
              <button
                key={period}
                onClick={() => setPeriodView(period)}
                className={`px-3 py-1 text-sm font-medium transition-colors capitalize ${
                  periodView === period
                    ? 'bg-blue-700 text-white dark:bg-blue-600'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {period}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3">
          <FuelGauge
            percentage={percentage}
            title={`${currentData.label} Goal Progress`}
            subtitle={currentData.subtitle}
          />
        </div>
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 h-full flex flex-col">
            <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Goal Details</h4>
            <div className="space-y-6 flex-grow">
              <div className="flex flex-col items-center text-center">
                <div className="text-sm text-gray-500 dark:text-gray-400">Closed Won Value</div>
                <div className={statusClasses.text + " text-2xl font-bold"}>
                  {formatCurrency(currentData.closedValue)}
                </div>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="text-sm text-gray-500 dark:text-gray-400">Quota Target</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-200">
                  {formatCurrency(currentData.quota)}
                </div>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="text-sm text-gray-500 dark:text-gray-400">Gap to Target</div>
                <div className={`text-2xl font-bold ${currentData.closedValue >= currentData.quota ? 'text-green-700 dark:text-green-500' : 'text-red-700 dark:text-red-500'}`}>
                  {currentData.closedValue >= currentData.quota
                    ? `+${formatCurrency(currentData.closedValue - currentData.quota)}`
                    : `-${formatCurrency(currentData.quota - currentData.closedValue)}`}
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-col items-center text-center">
                <div className="text-sm text-gray-500 dark:text-gray-400">Closed Won Deals</div>
                <div className="text-xl font-semibold text-gray-900 dark:text-gray-200">
                  {getDealsInPeriod(periodView).length} deals
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoalTracker;